/**
 * Catalog entries for @/components/ds — the parameterized component library.
 *
 * These entries carry `props` and `examples` because that is the whole point of
 * the ds/ layer: the components take parameters, and a catalog that only shows
 * one specimen hides them. Each example is named for the parameter it proves.
 */
import { ListChecks } from 'lucide-react';
import {
  Tooltip,
  Avatar,
  AvatarGroup,
  DataTable,
  Panel,
  DescriptionList,
  Field,
  TextInput,
  TextArea,
  Select,
  Radio,
  ChoiceGroup,
  Skeleton,
  SkeletonText,
  EmptyState,
  Callout,
  ValueMark,
} from '../../components/ds';
import { btnSecondary, status } from '../../components/mono/primitives';
import type { CatalogEntry } from './componentCatalog.types';
import {
  SortableSelectableTable,
  SwitchDemo,
  CheckboxDemo,
  ModalDemo,
  DisclosureDemo,
  TabsDemo,
  ProgressDemo,
} from './componentCatalog.harness';
import { ROWS, COLUMNS, ACCENT } from './componentCatalog.fixtures';

/* ── Entries ────────────────────────────────────────────────────────────── */

export const DS_CATALOG: CatalogEntry[] = [
  {
    id: 'ds-data-table',
    name: 'DataTable',
    tier: 'organism',
    status: 'live',
    importPath: '@/components/ds',
    description:
      'The parameterized semantic table. Replaces the 106 files that hand-roll a <thead> — whose header cell alone has twelve incompatible class strings in the tree, most with no dark-mode variant. Not a replacement for collection/EntityTable, which is the virtualized grid for unbounded row counts.',
    usage: `<DataTable
  columns={[
    { key: 'item', header: 'Item', width: '40%' },
    { key: 'score', header: 'Score', numeric: true, sortable: true },
  ]}
  rows={rows}
  getRowKey={(r) => r.id}
  density="compact"
  showHeader={false}
/>`,
    render: () => <DataTable columns={COLUMNS} rows={ROWS} getRowKey={(r) => r.id} />,
    examples: [
      {
        name: 'showHeader={false}',
        description: 'The parameter every hand-rolled table hard-codes. A headerless table for a summary block where the columns are self-evident.',
        render: () => <DataTable columns={COLUMNS} rows={ROWS.slice(0, 3)} getRowKey={(r) => r.id} showHeader={false} />,
      },
      {
        name: 'density="compact" · zebra · rule="grid"',
        description: 'Dense scan-and-compare reading. Vertical rules turn the table into a matrix; zebra banding carries the eye across wide rows.',
        render: () => (
          <DataTable columns={COLUMNS} rows={ROWS} getRowKey={(r) => r.id} density="compact" zebra rule="grid" />
        ),
      },
      {
        name: 'density="comfortable" · getRowAccent · footer',
        description: 'The deck’s accent bar carrying semantic state, drawn as an inset shadow so the unlayered dark border rule cannot flatten it.',
        render: () => (
          <DataTable
            columns={COLUMNS}
            rows={ROWS}
            getRowKey={(r) => r.id}
            density="comfortable"
            rule="none"
            getRowAccent={(r) => ACCENT[r.state]}
            footer={<span className="font-mono text-xs uppercase tracking-[0.12em]">4 items · 2 on track</span>}
          />
        ),
      },
      {
        name: 'sort · selectable',
        description: 'Sortable columns announce aria-sort; the header checkbox carries a real indeterminate state.',
        render: () => <SortableSelectableTable />,
      },
      {
        name: 'loading · empty',
        description: 'Both states are built in, so no caller improvises a spinner row or a centred placeholder.',
        render: () => (
          <div className="flex flex-col gap-4">
            <DataTable columns={COLUMNS} rows={[]} getRowKey={() => ''} loading skeletonRows={3} />
            <DataTable
              columns={COLUMNS}
              rows={[]}
              getRowKey={() => ''}
              empty="No items have been tossed into this cycle yet."
            />
          </div>
        ),
      },
    ],
    props: [
      { name: 'columns', type: 'Column<T>[]', required: true, description: 'Column definitions. Each takes key, header, render, align, width, numeric, sortable, sortKey, hidden.' },
      { name: 'rows', type: 'T[]', required: true, description: 'Row data. Cells fall back to String(row[key]) when a column has no render.' },
      { name: 'getRowKey', type: '(row, i) => string', required: true, description: 'Stable row identity — also the key used by selection.' },
      { name: 'showHeader', type: 'boolean', default: 'true', description: 'Draw the header row.' },
      { name: 'density', type: "'compact' | 'default' | 'comfortable'", default: "'default'", description: 'Cell padding. compact for dense grids, comfortable for short tables that carry weight.' },
      { name: 'rule', type: "'none' | 'horizontal' | 'grid'", default: "'horizontal'", description: 'Rule style. grid adds vertical rules for matrix reading.' },
      { name: 'zebra', type: 'boolean', default: 'false', description: 'Alternate row tint.' },
      { name: 'bordered', type: 'boolean', default: 'true', description: 'Wrap in a bordered panel. Turn off inside an existing Panel.' },
      { name: 'sort', type: 'SortSpec | null', default: 'null', description: 'Controlled sort state — { key, direction }.' },
      { name: 'onSortChange', type: '(next: SortSpec) => void', description: 'Required for a column’s sortable flag to render a control.' },
      { name: 'onRowClick', type: '(row, i) => void', description: 'Makes rows pressable and adds the hover tint.' },
      { name: 'selectable', type: 'boolean', default: 'false', description: 'Adds the checkbox column and the select-all header box.' },
      { name: 'selectedKeys', type: 'Set<string>', description: 'Controlled selection.' },
      { name: 'onSelectionChange', type: '(next: Set<string>) => void', description: 'Selection callback.' },
      { name: 'getRowAccent', type: '(row, i) => Tone | undefined', description: 'Coloured left edge per row for semantic state.' },
      { name: 'stickyHeader', type: 'boolean', default: 'false', description: 'Pins the header. Needs maxHeight to have anything to stick within.' },
      { name: 'maxHeight', type: 'string | number', description: 'Caps height and turns on vertical scroll.' },
      { name: 'loading', type: 'boolean', default: 'false', description: 'Renders skeleton rows instead of data.' },
      { name: 'skeletonRows', type: 'number', default: '5', description: 'How many skeleton rows while loading.' },
      { name: 'empty', type: 'ReactNode', default: "'Nothing to show.'", description: 'Shown when rows is empty and not loading.' },
      { name: 'footer', type: 'ReactNode', description: 'Summary row rendered in a <tfoot>.' },
      { name: 'caption', type: 'ReactNode', description: 'Accessible description; add captionHidden to keep it screen-reader-only.' },
      { name: 'ariaLabel', type: 'string', description: 'Accessible name when there is no visible caption.' },
    ],
  },

  {
    id: 'ds-panel',
    name: 'Panel',
    tier: 'organism',
    status: 'live',
    importPath: '@/components/ds',
    description:
      'A titled Square/Mono surface. mono/primitives’ panel() helper returns a class string for a bare box; this owns the header — eyebrow, title, count, right-aligned actions — so that relationship is decided once instead of rebuilt per page.',
    usage: `<Panel label="Cycle" title="Sessions" count={8} actions={<button {...btnSecondary()}>Add</button>} flush>
  <DataTable … bordered={false} />
</Panel>`,
    render: () => (
      <Panel
        label="Cycle 14"
        title="Open sessions"
        count={3}
        actions={<button type="button" {...btnSecondary()}>Add</button>}
        className="w-full"
      >
        <p className="text-sm text-[var(--text-sec)]">Body content sits on the raised ground.</p>
      </Panel>
    ),
    examples: [
      {
        name: 'flush + DataTable',
        description: 'flush drops the body padding so a table reaches the panel edges. The table sets bordered={false} to avoid a double rule.',
        render: () => (
          <Panel label="Ranked" title="Priority list" flush className="w-full">
            <DataTable columns={COLUMNS} rows={ROWS.slice(0, 3)} getRowKey={(r) => r.id} bordered={false} density="compact" />
          </Panel>
        ),
      },
      {
        name: 'accent · inset',
        description: 'accent carries semantic state on the panel edge; inset gives a quieter ground for a panel nested inside another.',
        render: () => (
          <div className="flex w-full flex-col gap-3">
            <Panel accent="coral" label="Blocked" title="Dependency failed">
              <p className="text-sm text-[var(--text-sec)]">Two items cannot be ranked until this clears.</p>
            </Panel>
            <Panel inset label="Nested" title="Inset panel">
              <p className="text-sm text-[var(--text-sec)]">Quieter ground for a panel inside a panel.</p>
            </Panel>
          </div>
        ),
      },
    ],
    props: [
      { name: 'label', type: 'ReactNode', description: 'Mono eyebrow above the title.' },
      { name: 'title', type: 'ReactNode', description: 'Panel title. Rendered as an h2 with an inline colour (the unlayered .dark h1–h6 rule would otherwise win).' },
      { name: 'actions', type: 'ReactNode', description: 'Right-aligned header cluster — buttons, a status readout, a menu.' },
      { name: 'count', type: 'number', description: 'Small count beside the title for a panel holding a list.' },
      { name: 'flush', type: 'boolean', default: 'false', description: 'Drop body padding so a table or row list reaches the edges.' },
      { name: 'inset', type: 'boolean', default: 'false', description: 'Quieter ground for a panel nested inside another panel.' },
      { name: 'accent', type: 'Tone', description: 'Coloured left edge for semantic state.' },
      { name: 'bodyClassName', type: 'string', description: 'Extra classes on the body wrapper only.' },
    ],
  },

  {
    id: 'ds-description-list',
    name: 'DescriptionList',
    tier: 'molecule',
    status: 'live',
    importPath: '@/components/ds',
    description:
      'Labelled facts as a real <dl>. Replaces 30 hand-rolled blocks. The element matters: screen readers pair each label with its value, which a grid of <div>s never does.',
    usage: `<DescriptionList columns={3} items={[{ label: 'Owner', value: 'Nakamura' }]} />`,
    render: () => (
      <DescriptionList
        className="w-full"
        columns={3}
        items={[
          { label: 'Owner', value: 'Nakamura' },
          { label: 'Altitude', value: '2 — Division' },
          { label: 'State', value: <span {...status('green')}>On track</span> },
        ]}
      />
    ),
    examples: [
      {
        name: 'layout="inline" · divided',
        description: 'Label to the left with scan rules — for a dense sidebar where vertical space is the constraint.',
        render: () => (
          <DescriptionList
            className="w-full"
            columns={1}
            layout="inline"
            divided
            items={[
              { label: 'Cycle', value: 'FY26 Q3' },
              { label: 'Facilitator', value: 'Okonkwo' },
              { label: 'Participants', value: '14' },
            ]}
          />
        ),
      },
    ],
    props: [
      { name: 'items', type: 'DescriptionItem[]', required: true, description: 'Each is { label, value, wide? }. wide spans the full width for a long value.' },
      { name: 'columns', type: '1 | 2 | 3 | 4', default: '2', description: 'Responsive column count.' },
      { name: 'layout', type: "'stacked' | 'inline'", default: "'stacked'", description: 'Label above the value, or to its left.' },
      { name: 'divided', type: 'boolean', default: 'false', description: 'Hairline between rows for a long list that needs scan lines.' },
    ],
  },

  {
    id: 'ds-field',
    name: 'Field / TextInput / TextArea / Select',
    tier: 'molecule',
    status: 'live',
    importPath: '@/components/ds',
    description:
      'Square/Mono form controls. Select wraps the NATIVE element deliberately — 524 raw <select> elements live across 246 files precisely because ui/AppSelect is a custom popover that changes the markup, cannot hold <optgroup>, and drops platform keyboard and mobile behaviour. This is a drop-in restyle instead.',
    usage: `<Field label="Workspace" hint="Shown to participants." required>
  <TextInput placeholder="Northwind Q3" />
</Field>`,
    render: () => (
      <div className="flex w-full max-w-sm flex-col gap-4">
        <Field label="Workspace name" hint="Shown to facilitators and participants." required>
          <TextInput placeholder="Northwind Q3 planning" />
        </Field>
        <Field label="Method">
          <Select
            options={[
              { value: 'ahp', label: 'Pairwise (AHP)' },
              { value: 'fbd', label: 'Forced bucket distribution' },
            ]}
            placeholder="— Choose a method —"
          />
        </Field>
      </div>
    ),
    examples: [
      {
        name: 'error · orientation="horizontal"',
        description: 'Field clones its child to wire id, aria-describedby, required and invalid — so the control never has to repeat them. Horizontal is for dense settings panels.',
        render: () => (
          <div className="flex w-full max-w-lg flex-col gap-4">
            <Field label="Email" error="That address is already in use.">
              <TextInput type="email" defaultValue="kaleo@kaleo" />
            </Field>
            <Field label="Cycle name" orientation="horizontal" hint="Appears in the ledger.">
              <TextInput defaultValue="FY26 Q3" size="sm" />
            </Field>
            <Field label="Rationale">
              <TextArea rows={2} placeholder="Why this, why now." />
            </Field>
          </div>
        ),
      },
    ],
    props: [
      { name: 'label', type: 'ReactNode', required: true, description: 'Field label. Rendered as a mono eyebrow.' },
      { name: 'hint', type: 'ReactNode', description: 'Explanatory text below the control. Hidden while error is present.' },
      { name: 'error', type: 'ReactNode', description: 'Error text. Also sets invalid on the cloned control, turning its rule coral.' },
      { name: 'required', type: 'boolean', default: 'false', description: 'Marks the control required and draws the marker beside the label.' },
      { name: 'orientation', type: "'vertical' | 'horizontal'", default: "'vertical'", description: 'Label above the control, or to its left for dense settings panels.' },
      { name: 'size (controls)', type: "'sm' | 'md'", default: "'md'", description: 'md is h-9, matching BTN_BASE so a field and a button line up.' },
      { name: 'options (Select)', type: '{ value, label, disabled? }[]', description: 'Convenience for flat lists. Ignored when children are given, so <optgroup> keeps working.' },
    ],
  },

  {
    id: 'ds-choice',
    name: 'Checkbox / Radio / Switch / ChoiceGroup',
    tier: 'atom',
    status: 'live',
    importPath: '@/components/ds',
    description:
      'Choice controls with labels and descriptions built in — 192 raw checkboxes across 97 files each carried their own markup. Checkbox vs Switch is enforced as a semantic split: a Switch takes effect the moment it is flipped and so has no indeterminate state and no group. If flipping it does not do something immediately, it is a Checkbox.',
    usage: `<ChoiceGroup legend="Notify on">
  <Checkbox checked={v} onChange={e => set(e.target.checked)} label="Ratification" />
</ChoiceGroup>`,
    render: () => <CheckboxDemo />,
    examples: [
      {
        name: 'Switch',
        description: 'Immediate effect, so no indeterminate state. Disabled carries the reason in its description where there is one.',
        render: () => <SwitchDemo />,
      },
      {
        name: 'Radio in a horizontal group',
        description: 'ChoiceGroup renders a real fieldset/legend, so screen readers announce the group name with each option.',
        render: () => (
          <ChoiceGroup legend="Visibility" orientation="horizontal">
            <Radio name="vis" defaultChecked label="Everyone" />
            <Radio name="vis" label="Facilitators" />
            <Radio name="vis" label="Nobody" />
          </ChoiceGroup>
        ),
      },
    ],
    props: [
      { name: 'label', type: 'ReactNode', description: 'Clickable label beside the control.' },
      { name: 'description', type: 'ReactNode', description: 'Secondary line under the label.' },
      { name: 'indeterminate', type: 'boolean', default: 'false', description: 'Checkbox only — a partially selected parent.' },
      { name: 'checked / onChange', type: 'boolean / (next) => void', description: 'Switch is fully controlled and hands back the next boolean, not an event.' },
      { name: 'legend (ChoiceGroup)', type: 'ReactNode', required: true, description: 'Group name, announced with every option.' },
      { name: 'orientation (ChoiceGroup)', type: "'vertical' | 'horizontal'", default: "'vertical'", description: 'Row layout for two or three short options.' },
    ],
  },

  {
    id: 'ds-feedback',
    name: 'Skeleton / EmptyState / Callout',
    tier: 'molecule',
    status: 'live',
    importPath: '@/components/ds',
    description:
      'What a surface shows when it is not showing data. 49 files improvise an animate-pulse bar and 65 hand-roll a centred placeholder — both load-bearing, since an empty state is often the first thing a new user sees.',
    usage: `<EmptyState icon={ListChecks} title="No items yet" description="…" action={<button {...btnSecondary()}>Add one</button>} />`,
    render: () => (
      <div className="flex w-full flex-col gap-5">
        <EmptyState
          icon={ListChecks}
          title="No items in this cycle"
          description="Items arrive when a parent unit tosses them down, or when you add one directly."
          action={<button type="button" {...btnSecondary()}>Add an item</button>}
        />
      </div>
    ),
    examples: [
      {
        name: 'Callout — the deck’s accent bar',
        description: 'Tinted ground with a coloured left edge, drawn as an inset shadow. Coral gets role="alert" automatically.',
        render: () => (
          <div className="flex w-full flex-col gap-3">
            <Callout tone="mustard" label="The decision layer" title="Priority drift">
              What the organization works on slides away from what it decided mattered.
            </Callout>
            <Callout tone="coral" label="Blocked">A dependency this item relies on has failed.</Callout>
            <Callout tone="green" label="Ratified">The proposal is binding as of this cycle.</Callout>
          </div>
        ),
      },
      {
        name: 'Skeleton',
        description: 'SkeletonText makes its last line short, which is what makes it read as a paragraph rather than a stack of bars.',
        render: () => (
          <div className="flex w-full max-w-sm items-start gap-3">
            <Skeleton circle width="2rem" />
            <SkeletonText lines={3} className="flex-1" />
          </div>
        ),
      },
    ],
    props: [
      { name: 'title (EmptyState)', type: 'ReactNode', required: true, description: 'What is absent, in the user’s words.' },
      { name: 'action (EmptyState)', type: 'ReactNode', description: 'The one thing to do next. An empty state without an action is a dead end.' },
      { name: 'size (EmptyState)', type: "'inline' | 'page'", default: "'inline'", description: 'inline inside a panel or table; page for a whole empty screen.' },
      { name: 'tone (Callout)', type: 'Tone', default: "'neutral'", description: 'green | mustard | slate | coral | neutral. coral also sets role="alert".' },
      { name: 'label (Callout)', type: 'ReactNode', description: 'Mono eyebrow above the body — the deck’s label column.' },
      { name: 'lines (SkeletonText)', type: 'number', default: '3', description: 'Placeholder line count.' },
      { name: 'circle (Skeleton)', type: 'boolean', default: 'false', description: 'Avatar or icon placeholder instead of a bar.' },
    ],
  },

  {
    id: 'ds-disclosure',
    name: 'Disclosure / DisclosureGroup',
    tier: 'molecule',
    status: 'live',
    importPath: '@/components/ds',
    description:
      'A section that expands and collapses. Was the largest measured drift left in the tree: 556 implementations across 156 files, each owning its own boolean, its own chevron rotation, and almost always no aria-expanded — so a screen reader was never told the section could open.',
    usage: `<DisclosureGroup>
  <Disclosure title="Participants" label="Configure" meta={14} defaultOpen>…</Disclosure>
</DisclosureGroup>`,
    render: () => <DisclosureDemo />,
    props: [
      { name: 'title', type: 'ReactNode', required: true, description: 'Summary row title.' },
      { name: 'label', type: 'ReactNode', description: 'Mono eyebrow above the title.' },
      { name: 'meta', type: 'ReactNode', description: 'Right-aligned summary content — a count or status. Not buttons: the whole row is the toggle.' },
      { name: 'defaultOpen', type: 'boolean', default: 'false', description: 'Uncontrolled initial state. Uncontrolled is why callers rolled their own — holding state for a visual toggle is more work than useState.' },
      { name: 'open / onOpenChange', type: 'boolean / (next) => void', description: 'Controlled mode. Pass both.' },
      { name: 'accent', type: 'Tone', description: 'Coloured left edge for semantic state.' },
      { name: 'divided', type: 'boolean', default: 'false', description: 'Hairline above the summary row. DisclosureGroup applies this for you.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Non-toggleable.' },
    ],
  },

  {
    id: 'ds-tabs',
    name: 'Tabs / TabPanel',
    tier: 'organism',
    status: 'live',
    importPath: '@/components/ds',
    description:
      'A keyboard-reachable tab strip with optional phase grouping. 352 activeTab implementations live across 35 files and exactly ONE file in src/ uses role="tab" at all — so almost every tab strip in the app is a row of buttons that arrow keys do nothing to. Roving tabindex per the WAI-ARIA pattern: only the active tab is in the tab order; ←/→/Home/End move between them.',
    usage: `<Tabs
  active={tab}
  onChange={setTab}
  ariaLabel="Session sections"
  groups={[{ label: 'Launch', tone: 'text-teal-500', tabs: [{ id: 'run', label: 'Run', icon: Play }] }]}
/>
<TabPanel id="run" active={tab}>…</TabPanel>`,
    render: () => <TabsDemo />,
    props: [
      { name: 'groups', type: 'TabGroup[]', required: true, description: 'Each is { label?, tone?, tabs }. A long strip is not a flat list — session detail carries 15 tabs that are really 5 phases, and drawing that is what makes 15 tabs legible. A single unlabelled group renders as a plain strip.' },
      { name: 'active / onChange', type: 'Id / (id) => void', required: true, description: 'Controlled selection.' },
      { name: 'tabs[].icon', type: 'ComponentType', description: 'Lucide icon, tinted by the group tone when inactive.' },
      { name: 'tabs[].count', type: 'number', description: 'Small number beside the label. Omit rather than passing 0 — a zero reads as a defect on a tab that is simply empty.' },
      { name: 'tabs[].disabled', type: 'boolean', default: 'false', description: 'Skipped by arrow-key navigation.' },
      { name: 'ariaLabel', type: 'string', description: 'Accessible name for the tab set.' },
    ],
  },

  {
    id: 'ds-modal',
    name: 'Modal',
    tier: 'organism',
    status: 'live',
    importPath: '@/components/ds',
    description:
      'A focus-trapped dialog over a scrim, with NO provider required. This does not replace panels/ModalPanel — that one keeps the managed, minimizable panel system. But it needs PanelProvider, which is why it has 3 importers while 152 files hand-roll `fixed inset-0 bg-black`. A dialog you cannot render without wiring a provider is a dialog nobody uses.',
    usage: `<Modal open={open} onClose={close} title="Retire this session?" footer={…}>…</Modal>`,
    render: () => <ModalDemo />,
    props: [
      { name: 'open / onClose', type: 'boolean / () => void', required: true, description: 'Controlled visibility.' },
      { name: 'title', type: 'ReactNode', required: true, description: 'Dialog title, wired as aria-labelledby.' },
      { name: 'description', type: 'ReactNode', description: 'Sentence under the title, wired as aria-describedby.' },
      { name: 'label', type: 'ReactNode', description: 'Mono eyebrow above the title.' },
      { name: 'footer', type: 'ReactNode', description: 'Right-aligned action cluster along the bottom edge.' },
      { name: 'size', type: "'sm' | 'md' | 'lg' | 'xl'", default: "'md'", description: 'Max width.' },
      { name: 'dismissible', type: 'boolean', default: 'true', description: 'False removes the close affordance and the scrim/Escape exits — for a dialog the user must resolve.' },
    ],
  },

  {
    id: 'ds-indicators',
    name: 'Tooltip / Avatar / ProgressBar',
    tier: 'atom',
    status: 'live',
    importPath: '@/components/ds',
    description:
      'Tooltip replaces the 2,113 native title="" attributes across 560 files. title is not a tooltip: it never appears on touch, cannot be styled, has an uncontrollable delay, and screen-reader support is inconsistent. This shows on hover AND focus — a hover-only tooltip is unreachable by keyboard — and describes its control rather than renaming it.',
    usage: `<Tooltip content="Altitude governs rank comparability."><AltitudeIndicator level={3} /></Tooltip>`,
    render: () => (
      <div className="flex flex-wrap items-center gap-6">
        <Tooltip content="Shows on hover and on keyboard focus.">
          <button type="button" {...btnSecondary()}>Hover or focus me</button>
        </Tooltip>
        <Avatar name="Chidi Okonkwo" />
        <Avatar name="Ingrid Lindqvist" ring="green" />
        <AvatarGroup names={['Chidi Okonkwo', 'Ingrid Lindqvist', 'Yuki Nakamura', 'Ada Abara', 'Tomas Reyes']} />
      </div>
    ),
    examples: [
      {
        name: 'ProgressBar',
        description: 'Carries role="progressbar" and its value bounds, which almost none of the 34 hand-built bars did. format overrides the readout.',
        render: () => <ProgressDemo />,
      },
    ],
    props: [
      { name: 'content (Tooltip)', type: 'ReactNode', required: true, description: 'Keep it short — this is not a place for a paragraph.' },
      { name: 'placement (Tooltip)', type: "'top' | 'bottom'", default: "'top'", description: 'Which side the tip sits on.' },
      { name: 'name (Avatar)', type: 'string', required: true, description: 'Full name. Initials come from first + last, not the first two letters, and it is the accessible name.' },
      { name: 'ring (Avatar)', type: 'Tone', description: 'Semantic ring — for presence or state, never decoration.' },
      { name: 'max (AvatarGroup)', type: 'number', default: '4', description: 'Show at most this many, then a +N chip.' },
      { name: 'value / max (ProgressBar)', type: 'number', required: true, description: 'Clamped to range; max defaults to 100.' },
      { name: 'format (ProgressBar)', type: '(value, max) => string', description: 'Override the readout. Default is "value / max".' },
      { name: 'showValue (ProgressBar)', type: 'boolean', default: 'false', description: 'Render the readout beside the label.' },
    ],
  },

  {
    id: 'ds-value-mark',
    name: 'ValueMark',
    tier: 'atom',
    status: 'live',
    importPath: '@/components/ds',
    description:
      'Who decided this value. One small icon beside every official value an AI can judge — criterion values, category and altitude. ✦ means an AI was part of it and the small mark on it says how; a plain person means no AI. States differ by shape, not color alone. The tooltip names the person and the date; lib/valueMark builds the words so every surface says the same thing.',
    usage: `const view = valueMarkFor(valueStatus(itemId, key, accepted, proposals, replacements), setting, canJudge, names);\n{view && <ValueMark {...view} />}`,
    render: () => (
      <div className="flex flex-wrap items-center gap-6 text-sm text-[var(--text-sec)]">
        <span className="inline-flex items-center gap-1.5"><ValueMark state="human" tip="Human · set by Ada Abara, 10/14/2026" /> Human decided</span>
        <span className="inline-flex items-center gap-1.5"><ValueMark state="ai_approved" tip="AI recommended · approved by Ada Abara, 10/14/2026" /> AI recommended, human approved</span>
        <span className="inline-flex items-center gap-1.5"><ValueMark state="ai_rule" tip="AI decided · by the rule Ada Abara set on 10/1/2026" /> AI decided</span>
        <span className="inline-flex items-center gap-1.5"><ValueMark state="ai_overridden" tip="Changed by Tomas Reyes, 10/15/2026 · replaced Value Estimator's M" /> AI decided, human overridden</span>
        <span className="inline-flex items-center gap-1.5"><ValueMark state="ai_waiting" tip="Value Estimator recommends L · waiting for a person" /> AI recommends · waiting</span>
      </div>
    ),
    examples: [
      {
        name: 'waitingTip',
        description: 'A value can stand while a newer AI recommendation waits: both marks are drawn.',
        render: () => <ValueMark state="ai_approved" tip="AI recommended · approved by Ada Abara, 10/14/2026" waitingTip="Value Estimator recommends XL · waiting for a person" />,
      },
    ],
    props: [
      { name: 'state', type: "'human' | 'ai_approved' | 'ai_rule' | 'ai_overridden' | 'ai_waiting'", required: true, description: 'Who decided the value (lib/valueMark).' },
      { name: 'tip', type: 'string', required: true, description: 'The words, and the accessible name. Build it with valueMarkFor / classificationMark.' },
      { name: 'waitingTip', type: 'string | null', description: 'A newer recommendation waiting while the value stands — drawn as a second, hollow ✦.' },
      { name: 'size', type: 'number', default: '14', description: 'Icon size in px; no bigger than the text beside it.' },
    ],
  },
];
